/**
 * Shared (platform-agnostic) logic for LIVE Nova runs: used by the web run
 * panel, the native run sheet, and both run-history views. Rendering stays in
 * the platform files; everything here is data.
 *
 * A run is watched by polling GET /runs/:id until it reaches a settled state,
 * with the per-user `nova:run` socket event as an instant "refetch now" nudge
 * (the poll is the reliable backbone; the socket makes it snappy).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';
import { SOCKET_EVENTS } from '@tabbio/types';
import type { NovaRunProgressEvent } from '@tabbio/types';
import { getSocket, subscribeSocketLifecycle } from '../../lib/socket';
import {
  getRun,
  type NovaRunDetail,
  type NovaRunDigest,
  type NovaRunMatchItem,
} from '../../lib/automation-api';
import { planRunWatchFailure } from './nova-run-watch-policy';

export {
  classifyRunWatchError,
  planRunWatchFailure,
  runErrorSummaryText,
  runFailureHint,
  runFailureReasonKey,
  skipReasonKey,
  visibleRunErrorSummary,
} from './nova-run-watch-policy';

/** Poll cadence while the run is executing / not yet settled. */
const ACTIVE_POLL_MS = 1200;

/**
 * Statuses where the run is no longer actively executing. `awaiting_approval`
 * counts as settled for POLLING purposes (the socket nudge still refreshes the
 * view when an approval elsewhere resumes the run).
 */
const SETTLED_RUN_STATUSES = new Set([
  'succeeded',
  'failed',
  'skipped',
  'killed',
  'awaiting_approval',
]);

export function isSettledRunStatus(status?: string | null): boolean {
  return typeof status === 'string' && SETTLED_RUN_STATUSES.has(status);
}

/** Truly terminal: the run can never move again. */
export function isTerminalRunStatus(status?: string | null): boolean {
  return isSettledRunStatus(status) && status !== 'awaiting_approval';
}

// ── Results extraction (what the run actually produced) ────────────────────────

function looksLikeMatch(value: unknown): value is NovaRunMatchItem {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as NovaRunMatchItem).catalogJobId === 'string' &&
    typeof (value as NovaRunMatchItem).jobTitle === 'string'
  );
}

/** All job matches across the run's step outputs, deduped by catalog job. */
export function extractRunMatches(
  detail: NovaRunDetail | null,
): NovaRunMatchItem[] {
  if (!detail?.steps) return [];
  const seen = new Set<string>();
  const out: NovaRunMatchItem[] = [];
  for (const step of detail.steps) {
    const matches = step.output?.matches;
    if (!Array.isArray(matches)) continue;
    for (const match of matches) {
      if (!looksLikeMatch(match) || seen.has(match.catalogJobId)) continue;
      seen.add(match.catalogJobId);
      out.push(match);
    }
  }
  return out;
}

/** The digest a compose_digest step stored, when the run has one. */
export function extractRunDigest(
  detail: NovaRunDetail | null,
): NovaRunDigest | null {
  if (!detail?.steps) return null;
  for (const step of detail.steps) {
    const digest = step.output?.digest as NovaRunDigest | undefined;
    if (digest && typeof digest.title === 'string') return digest;
  }
  return null;
}

// ── Checklist (planned steps ⋈ run steps) ──────────────────────────────────────

export interface NovaRunChecklistItem {
  stepId: string;
  type: string;
  /** 'pending' when the step has no row yet (not reached). */
  status: string;
  dryRun: boolean;
  output: Record<string, unknown> | null;
}

/**
 * The FULL run checklist: every planned step in order, with the live status of
 * the ones that have started. Falls back to the run-step rows alone for runs
 * whose detail predates plannedSteps.
 */
export function mergeRunSteps(detail: NovaRunDetail | null): NovaRunChecklistItem[] {
  if (!detail) return [];
  const actual = detail.steps ?? [];
  const byStepId = new Map(actual.map((s) => [s.stepId ?? s.id, s]));
  const planned = detail.plannedSteps ?? [];
  if (!planned.length) {
    return actual.map((s) => ({
      stepId: s.stepId ?? s.id,
      type: s.type,
      status: s.status,
      dryRun: Boolean(s.dryRun),
      output: s.output ?? null,
    }));
  }
  return planned.map((p) => {
    const row = byStepId.get(p.stepId);
    return {
      stepId: p.stepId,
      type: p.type,
      status: row?.status ?? 'pending',
      dryRun: Boolean(row?.dryRun ?? p.requiresApproval),
      output: row?.output ?? null,
    };
  });
}

// ── Labels / summaries (i18n-aware, component passes `t`) ──────────────────────

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Friendly, localizable step label: `nova.steps.<verb>` with a prettified fallback. */
export function stepLabel(t: Translate, type?: string | null): string {
  if (!type) return t('nova.steps.draft', { defaultValue: 'Draft' });
  if (type === 'connector.action') {
    return t('nova.steps.connectorAction', { defaultValue: 'Send via connector' });
  }
  const bare = type.replace(/^(nova|tabbio)\./, '');
  const fallback = bare
    .replace(/[._]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
  return t(`nova.steps.${bare}`, { defaultValue: fallback });
}

/** One-line "what this step produced", from its raw output. */
export function stepResultSummary(
  t: Translate,
  item: { status: string; dryRun: boolean; output: Record<string, unknown> | null },
): string | null {
  const output = item.output ?? {};
  // Research task steps (Tasks parity).
  switch (output.kind) {
    case 'research_web':
      // `sourceCount` counts sources the way the delivered report does, so
      // this line and Result details agree.
      return t('nova.results.sourcesFound', {
        count:
          typeof output.sourceCount === 'number'
            ? output.sourceCount
            : Array.isArray(output.sources)
              ? output.sources.length
              : 0,
      });
    case 'read_sources':
      return t('nova.results.sourcesRead', {
        count: typeof output.readCount === 'number' ? output.readCount : 0,
      });
    case 'compose_report':
      return output.quiet === true
        ? t('nova.results.nothingNew')
        : t('nova.results.reportReady');
    case 'render_document':
      if (output.skipped) return null;
      return output.status === 'ready'
        ? t('nova.results.documentReady')
        : t('nova.results.documentRendering');
    case 'deliver':
      return t('nova.results.delivered');
    default:
      break;
  }
  const matches = output.matches;
  if (Array.isArray(matches)) {
    return t('nova.results.matchesFound', { count: matches.length });
  }
  const digest = output.digest as NovaRunDigest | undefined;
  if (digest && typeof digest.itemCount === 'number') {
    return t('nova.results.digestReady', { count: digest.itemCount });
  }
  const summary = output.summary as { discovered?: number } | undefined;
  if (typeof summary?.discovered === 'number') {
    return t('nova.results.matchesFound', { count: summary.discovered });
  }
  if (item.dryRun && item.status === 'awaiting_approval') {
    return t('nova.results.draftAwaiting');
  }
  return null;
}

// ── The live watcher hook ──────────────────────────────────────────────────────

export interface NovaRunWatch {
  detail: NovaRunDetail | null;
  /** True while the latest fetch failed (the run may still have loaded before). */
  error: boolean;
  /**
   * The run is gone for this viewer (NOVA_NOT_FOUND / HTTP_404 / HTTP_403):
   * polling stopped for good. Render the "no longer available" state.
   */
  notFound: boolean;
  /**
   * Transient failures used up before anything loaded: polling stopped.
   * `refresh()` starts over.
   */
  exhausted: boolean;
  /** True while the run is still executing (drives spinners). */
  live: boolean;
  refresh: () => void;
}

/**
 * Watch a run live: immediate fetch, then poll every ~1.2s until the run
 * settles; a `nova:run` socket event for this run forces an immediate refetch
 * (including after settle, so an approval from another surface updates us).
 * A missing run stops after exactly one request; transient failures back off
 * (3s, 6s, 12s, 24s, then 30s) and give up after 5 without any success.
 */
export function useNovaRunWatch(runId: string | null | undefined): NovaRunWatch {
  const [detail, setDetail] = useState<NovaRunDetail | null>(null);
  const [error, setError] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const aliveRef = useRef(true);
  const runIdRef = useRef(runId);
  runIdRef.current = runId;
  /** Consecutive failed fetches since the last success (or restart). */
  const failuresRef = useRef(0);
  /** Any fetch for this run has succeeded. */
  const loadedRef = useRef(false);
  /** Polling stopped for good (the run is gone). */
  const stoppedRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const tick = useCallback(async (id: string) => {
    try {
      const next = await getRun(id);
      if (!aliveRef.current || runIdRef.current !== id) return;
      failuresRef.current = 0;
      loadedRef.current = true;
      setDetail(next);
      setError(false);
      setExhausted(false);
      if (!isSettledRunStatus(next.run.status)) {
        clearTimer();
        timerRef.current = setTimeout(() => void tick(id), ACTIVE_POLL_MS);
      }
    } catch (fetchError) {
      if (!aliveRef.current || runIdRef.current !== id) return;
      failuresRef.current += 1;
      const plan = planRunWatchFailure({
        error: fetchError,
        consecutiveFailures: failuresRef.current,
        hasLoaded: loadedRef.current,
      });
      clearTimer();
      setError(true);
      if (plan.notFound) {
        stoppedRef.current = true;
        setNotFound(true);
        return;
      }
      if (plan.exhausted) {
        setExhausted(true);
        return;
      }
      if (plan.retryInMs != null) {
        timerRef.current = setTimeout(() => void tick(id), plan.retryInMs);
      }
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    failuresRef.current = 0;
    loadedRef.current = false;
    stoppedRef.current = false;
    setDetail(null);
    setError(false);
    setNotFound(false);
    setExhausted(false);
    clearTimer();
    if (runId) {
      void tick(runId);
    }
    return () => {
      aliveRef.current = false;
      clearTimer();
    };
  }, [runId, tick]);

  // Track the socket singleton across auth-driven recreation so the nudge
  // listener below re-attaches to the live instance instead of a dead one.
  const [socket, setSocket] = useState<Socket | null>(null);
  useEffect(() => {
    if (!runId) return;
    setSocket(getSocket());
    return subscribeSocketLifecycle(setSocket);
  }, [runId]);

  // Socket nudge: any progress event for this run forces an immediate refetch.
  useEffect(() => {
    if (!runId || !socket) return;
    const onProgress = (event: NovaRunProgressEvent) => {
      // A run that is gone for this viewer is never refetched.
      if (event?.runId === runId && !stoppedRef.current) {
        clearTimer();
        void tick(runId);
      }
    };
    socket.on(SOCKET_EVENTS.NOVA_RUN, onProgress);
    return () => {
      socket.off(SOCKET_EVENTS.NOVA_RUN, onProgress);
    };
  }, [runId, tick, socket]);

  /** Start over: resets the failure budget and fetches immediately. */
  const refresh = useCallback(() => {
    if (runIdRef.current) {
      clearTimer();
      failuresRef.current = 0;
      stoppedRef.current = false;
      setNotFound(false);
      setExhausted(false);
      void tick(runIdRef.current);
    }
  }, [tick]);

  const live = Boolean(detail && !isSettledRunStatus(detail.run.status));
  return { detail, error, notFound, exhausted, live, refresh };
}
